import React, { useState, useEffect } from "react";
import { callBackend } from "../utils/api";
import { useNotify } from "./Notification";
import { Send, Users, ShieldAlert } from "lucide-react";

export default function BankTransfer({ stats, fetchStats }) {
  const { addNotification } = useNotify();
  const [friends, setFriends] = useState([]);
  const [targetId, setTargetId] = useState("");
  const [amount, setAmount] = useState("");
  const [loading, setLoading] = useState(false);

  // Load verified crew members for the dropdown
  useEffect(() => {
    const loadFriends = async () => {
      try {
        const data = await callBackend("/social/friends");
        // Only allow transfers to 'accepted' friends
        setFriends(data.filter((f) => f.status === "accepted"));
      } catch (err) {
        console.error("Failed to load crew for transfers", err);
      }
    };
    loadFriends();
  }, []);

  const handleTransfer = async (e) => {
    e.preventDefault();
    if (!targetId || !amount) return;

    setLoading(true);
    try {
      const result = await callBackend("/bank/transfer", {
        method: "POST",
        body: {
          targetId: parseInt(targetId),
          amount: parseInt(amount),
          clientStats: stats, // Required for backend security check
        },
      });

      addNotification(result.message, "success");
      setAmount("");
      setTargetId("");
      fetchStats(); // Update vault balance in UI
    } catch (err) {
      addNotification(err.message, "error");
    } finally {
      setLoading(false);
    }
  };

  const transferFee = amount ? Math.ceil(parseInt(amount) * 0.02) : 0;

  return (
    <div className="bg-zinc-950 border-2 border-zinc-800 p-6 font-mono">
      <h3 className="text-xl font-black text-white uppercase italic mb-4 flex items-center gap-2">
        <Send size={20} className="text-blue-500" /> Bank Transfer (Wire)
      </h3>

      <form onSubmit={handleTransfer} className="space-y-4">
        {/* Recipient Selection */}
        <div className="space-y-1">
          <label className="text-[10px] text-zinc-500 uppercase font-bold">
            Select Recipient
          </label>
          <div className="relative">
            <Users
              size={16}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-600"
            />
            <select
              value={targetId}
              onChange={(e) => setTargetId(e.target.value)}
              className="w-full bg-zinc-900 border border-zinc-800 p-2 pl-10 text-white text-xs outline-none focus:border-blue-600 appearance-none"
            >
              <option value="">-- CHOOSE CREW MEMBER --</option>
              {friends.map((friend) => (
                <option key={friend.id} value={friend.id}>
                  {friend.username} (LVL {friend.level})
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Amount Input */}
        <div className="space-y-1">
          <label className="text-[10px] text-zinc-500 uppercase font-bold">
            Amount to Wire
          </label>
          <input
            type="number"
            value={amount}
            placeholder="0"
            onChange={(e) => setAmount(e.target.value)}
            className="w-full bg-zinc-900 border border-zinc-800 p-2 text-white text-xs outline-none focus:border-blue-600"
          />
        </div>

        {/* Fee and Risk Warning */}
        {amount > 0 && (
          <div className="bg-blue-900/10 border border-blue-900/30 p-3 space-y-2">
            <div className="flex justify-between text-[10px] uppercase font-bold">
              <span className="text-zinc-400">Transaction Fee (2%):</span>
              <span className="text-blue-400">
                ${transferFee.toLocaleString()}
              </span>
            </div>
            <div className="flex justify-between text-[10px] uppercase font-bold border-t border-blue-900/30 pt-1">
              <span className="text-zinc-400">Total Deduction:</span>
              <span className="text-white">
                ${(parseInt(amount) + transferFee).toLocaleString()}
              </span>
            </div>
            {amount >= 100000 && (
              <div className="flex items-center gap-2 text-[9px] text-orange-500 font-bold uppercase animate-pulse">
                <ShieldAlert size={12} /> High-value transfer will attract Heat
              </div>
            )}
          </div>
        )}

        <button
          disabled={loading || !targetId || !amount}
          className="w-full bg-blue-600 hover:bg-blue-500 disabled:bg-zinc-800 text-black font-black py-3 uppercase italic transition-all flex justify-center items-center gap-2"
        >
          {loading ? "PROCESSING WIRE..." : "AUTHORIZE TRANSFER"}
        </button>
      </form>
    </div>
  );
}
